import type { ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetHeader,
  ModalSheetContentBlock,
  ModalSheetContentTitle,
  ModalSheetFooterButtonGroup,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterSpacer,
  ModalSheetContentDescription,
  ModalSheetFooterSection,
  ModalSheetContentIllustration,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'
import { byDesign } from '@/infra/design'
import imgGetStartedUrl from '@/assets/misc/img_get-started.svg'
import CreateTeamIllustration from '@/components/SpotIllustration/CreateTeamIllustration'

export interface CompleteInfoGatheringDialogProps {
  showing: boolean
  onClose: () => unknown
  onComplete: () => unknown
  isCompletePending: boolean
  requestedProName?: string
  /**
   * Rendered in the footer only for admins.
   */
  completeAsAdminInput?: ReactNode
}

export function CompleteInfoGatheringDialog({
  showing,
  onClose,
  onComplete,
  isCompletePending,
  requestedProName,
  completeAsAdminInput,
}: CompleteInfoGatheringDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isCompletePending ? undefined : onClose}
      disableCloseOnOutsideClick
    >
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetContentIllustration
            src={imgGetStartedUrl}
            illustration={byDesign(undefined, CreateTeamIllustration)}
            alt="Let's get started"
          />
          <ModalSheetContentTitle>
            You are about to complete info gathering
          </ModalSheetContentTitle>
          <ModalSheetContentDescription>
            Are you sure you want to proceed? <br />
            The job will be routed to{' '}
            {requestedProName !== undefined ? (
              <b data-test={'complete-info-gathering-dialog-requested-pro'}>
                {requestedProName}
              </b>
            ) : (
              'the next available Pro'
            )}
            .
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
      </ModalSheetContent>
      <ModalSheetFooter>
        {completeAsAdminInput && (
          <ModalSheetFooterSection>
            {completeAsAdminInput}
          </ModalSheetFooterSection>
        )}
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            isDisabled={isCompletePending}
            testId="complete-info-gathering-dialog-close-button"
            variant="default"
            onPress={onClose}
          />
          <Button
            label="Complete"
            testId="complete-info-gathering-dialog-complete-button"
            variant="primary"
            onPress={onComplete}
            isPending={isCompletePending}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
